import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './useAuth';

/**
 * Simple beta feedback widget visibility hook
 * Implements George's recommended minimal approach
 */
export function useBetaFeedbackWidget() {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  // Update time periodically for dismissal checks
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, []);

  // For MVP: Show widget to all authenticated users
  const isBetaUser = isAuthenticated;

  // Static pages where widget should never appear
  const staticPages = [
    '/', '/about', '/contact', '/privacy', '/terms', '/faq'
  ];
  
  // Check if current page is a static/marketing page
  const isStaticPage = staticPages.some(page => 
    location.pathname === page || location.pathname.startsWith(page + '/')
  );

  // Simple localStorage for dismissal tracking
  const getDismissedUntil = (): number | null => {
    try {
      const stored = localStorage.getItem('oro_beta_feedback_dismissed_until');
      return stored ? parseInt(stored, 10) : null;
    } catch {
      return null;
    }
  };

  const getPermanentlyDismissed = (): boolean => {
    try {
      return localStorage.getItem('oro_beta_feedback_permanent') === 'true';
    } catch {
      return false;
    }
  };

  // Check if dismissal cooldown has passed
  const dismissedUntil = getDismissedUntil();
  const permanentlyDismissed = getPermanentlyDismissed();
  const cooldownPassed = !dismissedUntil || currentTime > dismissedUntil;

  // Simple visibility calculation
  const isVisible = isAuthenticated && 
                   isBetaUser && 
                   !isStaticPage && 
                   !permanentlyDismissed && 
                   cooldownPassed;

  // Dismissal functions
  const dismissSession = () => {
    try {
      // Hide for 4 hours
      const fourHours = currentTime + (4 * 60 * 60 * 1000);
      localStorage.setItem('oro_beta_feedback_dismissed_until', fourHours.toString());
    } catch {
      // Fallback to session storage if localStorage fails
      sessionStorage.setItem('oro_beta_feedback_session_dismissed', 'true');
    }
  };

  const dismissWeek = () => {
    try {
      // Hide for 1 week
      const oneWeek = currentTime + (7 * 24 * 60 * 60 * 1000);
      localStorage.setItem('oro_beta_feedback_dismissed_until', oneWeek.toString());
    } catch {
      // Fallback to session storage if localStorage fails
      sessionStorage.setItem('oro_beta_feedback_session_dismissed', 'true');
    }
  };

  const dismissPermanently = () => {
    try {
      localStorage.setItem('oro_beta_feedback_permanent', 'true');
    } catch {
      // Can't permanently dismiss if localStorage isn't available
      console.warn('Cannot permanently dismiss beta feedback widget: localStorage unavailable');
    }
  };

  return {
    isVisible,
    dismissSession,
    dismissWeek,
    dismissPermanently
  };
}